import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import BranchLicenseModal from '../../Components/Modals/BranchLicenseModal';

const BASE_URL = process.env.REACT_APP_API_URL;


function AuditorLoginPage() {
  const [formData, setFormData] = useState({
    emailAddress: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.emailAddress) newErrors.emailAddress = 'Email address is required';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (!validateForm()) return;
  
    setLoading(true);
  
    try {
      const response = await fetch(`${BASE_URL}/auditor-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        if (data.message === "Branch license has expired.") {
          setShowLicenseModal(true);
          setLoading(false);
          return;
        }
        throw new Error(data.message || "Login failed");
      }
  
      setLoading(false);
      setMessage("Login successful!");

      const auditorData = {
        id: data.auditor.id,
        firstName: data.auditor.firstName,
        lastName: data.auditor.lastName,
        email: data.auditor.emailAddress,
        role: data.auditor.role,
        BranchCode: data.auditor.BranchCode,
        accountType: data.auditor.accountType,
        plan: data.auditor.plan
      };
     
      localStorage.setItem('auditor', JSON.stringify(auditorData));

      setTimeout(() => {
        navigate("/InAuditorDashboard");
      }, 1500);

    } catch (error) {
      setLoading(false);
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <>
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Row className="w-100 justify-content-center">
          <Col md={5}>
            <Card className="shadow-lg border-0 rounded-lg mb-4" style={{ maxWidth: '400px', margin: 'auto' }}>
              <Card.Header className="bg-primary text-white text-center py-3">
                <h3 className="mb-0">Auditor Login</h3>
                <p className="text-white-50 mb-0">Access your auditor account</p>
              </Card.Header>
              <Card.Body className="p-4">
                {message && (
                  <Alert variant={message.startsWith("Error") ? "danger" : "success"} dismissible>
                    {message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Email Address</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                          <Form.Control
                            type="email"
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            isInvalid={!!errors.emailAddress}
                            placeholder="Enter email address"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.emailAddress}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Password</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <FaLock />
                          </span>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password"
                            required
                            isInvalid={!!errors.password}
                            className="form-control"
                          />
                          <button
                            type="button"
                            className="input-group-text"
                            onClick={togglePasswordVisibility}
                            style={{ cursor: 'pointer' }}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-grid gap-2">
                    <Button type="submit" variant="primary" size="m" disabled={loading}>
                      {loading ? "Logging in..." : "Login"}
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="m" 
                      onClick={() => navigate('/LoginPage')}
                    >
                      Back to Admin Login
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <BranchLicenseModal 
        show={showLicenseModal}
        onHide={() => setShowLicenseModal(false)}
      />
    </>
  );
}

export default AuditorLoginPage;